<template>
  <modal :name="nombreModal" :adaptive="true" height="auto" width="560px">
    <div class="conf">
      <div class="modal-header conf-cabecera">
        <h4>{{ $t("eliminarCategoria", { nombre: cat.nombre }) }}</h4>
      </div>

      <div class="modal-body conf-cuerpo">
        <!-- Lo que va a pasar, antes de pedir nada: la decision se toma sabiendo el
             precio. Mismo orden que EtiquetaBajaModal. -->
        <ul class="conf-consecuencias">
          <li v-if="cat.soloEn && cat.soloEn.length">
            {{ $t("bajaSoloEnClientes", { terminales: cat.soloEn.map(t => "«" + t + "»").join(", ") }) }}
          </li>
          <li v-if="productos">
            {{ $tc("eliminarConProductos", productos, { n: productos }) }}
          </li>
          <li v-else>
            {{ $tc("eliminarSinProductos", terminales, { n: terminales }) }}
          </li>
          <li>{{ $t("eliminarHuecoSeQueda") }}</li>
        </ul>

        <div class="conf-campo" v-if="productos">
          <label for="bajaCatDestino">{{ $t("eligeDestino") }}</label>

          <el-select
            id="bajaCatDestino"
            class="select-default conf-select"
            popper-class="baja-cat-desplegable"
            v-model="codeDestino"
            filterable
            :placeholder="$t('moverProductosA')"
            :no-match-text="$t('sinResultadosCategoria')"
          >
            <el-option-group
              v-for="g in destinoPorGrupo"
              :key="'g' + g.grupo"
              :label="g.grupo ? $t('grupoNumero', { g: g.grupo }) : $t('fueraDeLosGrupos')"
            >
              <el-option
                v-for="c in g.categorias"
                :key="'d' + c.code"
                :value="c.code"
                :label="c.nombre"
              >
                <span class="op-nombre">{{ c.nombre }}</span>
                <span class="op-num">{{ c.productos }}</span>
              </el-option>
            </el-option-group>
          </el-select>

          <!-- Dejarlos sin categoria es la salida de emergencia, no una opcion del
               desplegable: no es una categoria. Se marca a mano, y al marcarla aparece
               lo que implica. Esta casilla ES la segunda confirmacion que antes era un
               `swal` encadenado. -->
          <label class="conf-chk" :class="{ puesto: sinCategoria }">
            <input type="checkbox" v-model="sinCategoria" />
            {{ $t("dejarlosSinCategoria") }}
          </label>

          <p class="conf-nota" v-if="sinCategoria">
            {{ $tc("confirmarSinCategoriaTexto", productos, { n: productos }) }}
          </p>
        </div>

        <p class="conf-error" v-if="error">{{ error }}</p>
      </div>

      <div class="modal-footer conf-pie">
        <button type="button" class="btn btn-claro" :disabled="ocupado" @click="cerrar()">
          {{ $t("cancelar") }}
        </button>
        <button
          type="button"
          class="btn btn-rojo"
          :disabled="ocupado || !puedeConfirmar"
          @click="confirmar()"
        >
          {{ $t("eliminar") }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import { Select, Option, OptionGroup } from "element-ui";

/**
 * Eliminar una categoria del negocio, diciendo a donde van sus productos.
 *
 * Sustituye a los dos `swal` encadenados que habia antes -uno para elegir destino y
 * otro para confirmar el "sin categoria"-. Mismo planteamiento que EtiquetaBajaModal:
 * primero se ensena lo que va a pasar, y solo despues se pide la decision. La casilla
 * de "dejarlos sin categoria" ES la segunda confirmacion: hay que marcarla a mano, y
 * al marcarla aparece debajo lo que implica.
 *
 * Diferencia con las etiquetas, y es la que permite que aqui se pueda dejar el hueco
 * sin atrapar nada: la fila de `categories` NO se borra, solo se le vacia el nombre.
 * Los productos se quedan en ella, la pantalla los sigue mostrando en la fila
 * "Sin categoria" de su grupo, y ninguna otra categoria se recoloca.
 *
 * Igual que los modales de etiquetas, este componente NO escribe: emite `confirmar`
 * con {code, codeDestino} y la pantalla llama al endpoint.
 */
export default {
  name: "CategoriaBajaModal",

  props: {
    nombreModal: { type: String, default: "modalBajaCategoria" },
    // Todas las categorias del negocio, tal como las devuelve getBusinessCategories
    // (grupos[].categorias + fueraDeGrupo).
    categorias: { type: Array, default: () => [] },
    // Terminales vivos del negocio, solo para el plural del aviso.
    terminales: { type: Number, default: 0 },
    ocupado: { type: Boolean, default: false }
  },

  data() {
    return {
      cat: { code: "", nombre: "", productos: 0, grupo: 0 },
      // OJO: el code es una cadena y "0" es un code valido -es la primera tecla del
      // grupo 1-. Aqui "no se ha elegido nada" se representa con null y NUNCA con un
      // falsy, porque "0" es falsy en JS y se perderia esa categoria como destino.
      codeDestino: null,
      sinCategoria: false,
      error: ""
    };
  },

  computed: {
    productos() {
      return this.cat.productos || 0;
    },

    /**
     * Cualquier categoria del negocio con nombre, menos la que se elimina. Agrupadas
     * por grupo, que es el idioma de la pantalla: el usuario no ve teclas ni ranuras.
     */
    destinoPorGrupo() {
      let vm = this;
      let porGrupo = {};

      this.categorias.forEach(function(c) {
        // Ni las que el servidor no tiene: el destino se escribe en todos los terminales
        // y acabaria creandose en el servidor. La API lo rechaza igual.
        if (c.code === vm.cat.code || !c.nombre || (c.soloEn && c.soloEn.length)) {
          return;
        }

        let g = c.grupo || 0;

        if (!porGrupo[g]) {
          porGrupo[g] = { grupo: g, categorias: [] };
        }

        porGrupo[g].categorias.push(c);
      });

      return Object.keys(porGrupo)
        .sort(function(a, b) {
          // El 0 es "fuera de los cuatro grupos" y va al final, no al principio.
          if (Number(a) === 0) return 1;
          if (Number(b) === 0) return -1;
          return Number(a) - Number(b);
        })
        .map(function(g) {
          return porGrupo[g];
        });
    },

    hayDestinoElegido() {
      return this.codeDestino !== null && this.codeDestino !== undefined;
    },

    puedeConfirmar() {
      if (!this.productos) {
        return true;
      }

      return this.sinCategoria || this.hayDestinoElegido;
    }
  },

  watch: {
    // Son excluyentes: marcar "dejarlos sin categoria" descarta el destino elegido, y
    // elegir destino desmarca la casilla. Si no, no se sabe cual gana.
    sinCategoria(puesto) {
      if (puesto) {
        this.codeDestino = null;
      }
    },

    codeDestino(valor) {
      // Comparacion explicita contra null: `if (valor)` seria false para el code "0".
      if (valor !== null && valor !== undefined) {
        this.sinCategoria = false;
      }
    }
  },

  methods: {
    abrir(cat) {
      this.cat = cat;
      this.codeDestino = null;
      this.sinCategoria = false;
      this.error = "";

      this.$modal.show(this.nombreModal);
    },

    cerrar() {
      this.$modal.hide(this.nombreModal);
    },

    mostrarError(mensaje) {
      this.error = mensaje || this.$t("errorInesperado");
    },

    confirmar() {
      if (!this.puedeConfirmar) {
        return;
      }

      this.error = "";

      // El API espera codeDestino = "" para "se quedan donde estan, sin categoria".
      // Sin productos dentro no hay nada que mover, asi que tambien va vacio.
      let destino = "";

      if (this.productos && !this.sinCategoria && this.hayDestinoElegido) {
        destino = this.codeDestino;
      }

      this.$emit("confirmar", {
        code: this.cat.code,
        codeDestino: destino
      });
    }
  },

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [OptionGroup.name]: OptionGroup
  }
};
</script>

<style scoped>
@import "../confirmar-en-modal.css";
</style>

<!-- Sin `scoped`: element-ui cuelga el desplegable del `body`. Acotado por
     `popper-class`, asi que no toca ningun otro `el-select`. -->
<style>
.baja-cat-desplegable .el-select-dropdown__item {
    display: flex;
    align-items: center;
    gap: 12px;
}

.baja-cat-desplegable .op-nombre {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
}

.baja-cat-desplegable .op-num {
    flex: 0 0 auto;
    min-width: 28px;
    text-align: right;
    font-size: 0.85em;
    font-variant-numeric: tabular-nums;
    color: #98a2ad;
}

.baja-cat-desplegable .el-select-dropdown__item.selected .op-num,
.baja-cat-desplegable .el-select-dropdown__item.hover .op-num {
    color: #4a5765;
}

.baja-cat-desplegable .el-select-group__title {
    font-size: 0.72em;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #98a2ad;
}
</style>



// WEBPACK FOOTER //
// src/pages/Widgets/Categorias/CategoriaBajaModal.vue